import { useDispatch } from 'react-redux'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  IconButtonProps,
  Tooltip
} from '@mui/material'
import { Edit as EditIcon } from '@mui/icons-material'
import { hideSector, showSector } from '../redux/slices/sectors'
import { useTranslation } from 'react-i18next'
import { Project, Sector } from '../types'
import { useState } from 'react'
import SectorForm from './SectorForm'
import { ProjectStateType } from '../enums/ProjectStateType'
import { usePolicy } from '../hooks'

export interface SectorEditButtonProps extends Omit<IconButtonProps, 'onClick' | 'children'> {
  project?: Project
  sector: Sector
}

export default function SectorEditButton({ project, sector, ...props }: SectorEditButtonProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policies.
   */
  const { canEditProject } = usePolicy()

  /**
   * Indicates if the dialog is open.
   */
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  /**
   * Open the dialog.
   */
  function openDialog(): void {
    dispatch(showSector(sector))
    setIsDialogOpen(true)
  }

  /**
   * Close the dialog.
   */
  function closeDialog(): void {
    setIsDialogOpen(false)
    dispatch(hideSector())
  }

  return (
    <>
      {project && project.state !== ProjectStateType.ARCHIVED && canEditProject(project) && (
        <Tooltip title={t('edit')}>
          <IconButton size="small" {...props} onClick={() => openDialog()}>
            <EditIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}

      <Dialog open={isDialogOpen} maxWidth="sm" fullWidth onClose={() => closeDialog()}>
        <DialogTitle>{t('editSector')}</DialogTitle>
        <DialogContent>
          <SectorForm
            value={{ project: project?.id }}
            onCancel={() => closeDialog()}
            afterSubmit={() => closeDialog()}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
